import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import useLinkForColumnTypes from './useLinkForColumnTypes';
import formatLinksToGroupedTreemapChartData from '../../../../../utils/formatLinksToGroupedTreemapChartData';
import createTooltipMarkup from './tooltip-markup/tooltipMarkup';
import SectionWrapper from '../section-wrapper/SectionWrapper';
import { RESONANCE_SORT } from '../../../../../constants';
import HighPerformingAudiencesFilters from './filters/HighPerformingAudiencesFilters';
import GroupedTreemap from '../../../../../components/charts/echarts/grouped-treemap/GroupedTreemap';
import { getChartValueColor } from '../../../../../utils/chart';
import HighPerformingSubtitle from '../high-performing-subtitle/HighPerformingSubtitle';
import FilterColumnsSelect from './filters/FilterColumnsSelect';

function HighPerformingAudiences({ selectedMessage }) {
  const theme = useSelector((state) => state.global.theme);

  const [isGlobalPercentage, setIsGlobalPercentage] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [sortBy, setSortBy] = useState(RESONANCE_SORT.TOTAL);

  const valueColor = getChartValueColor(theme);

  const relatedLinks = useLinkForColumnTypes({
    columnTypes: selectedFilters.map((filter) => filter.value),
  });

  const mappedValueSelectors = {
    [RESONANCE_SORT.TOTAL]: (link) => (isGlobalPercentage ? link.globalTotal : link.total),
    [RESONANCE_SORT.R1]: (link) => (isGlobalPercentage ? link.globalR1 : link.r1),
    [RESONANCE_SORT.R2]: (link) => (isGlobalPercentage ? link.globalR2 : link.r2),
  };

  const chartsData = formatLinksToGroupedTreemapChartData(relatedLinks, mappedValueSelectors[sortBy]);

  const config = {
    tooltip: {
      formatter: (info) => {
        return createTooltipMarkup({ data: info.data, isGlobal: isGlobalPercentage });
      },
    },
    series: {
      label: {
        formatter: (params) => {
          const { name, value } = params;
          return value != null ? `${name} - ${value}%` : name;
        },
      },
      upperLabel: {
        show: true,
        height: 30,
      },
      levels: [
        {
          itemStyle: {
            borderWidth: 0,
            gapWidth: 0,
          },
          upperLabel: {
            show: false,
          },
        },
        {
          colorSaturation: [0.7, 0.4],
          itemStyle: {
            borderColorSaturation: 0.5,
            gapWidth: 2,
            borderWidth: 0,
          },
          upperLabel: {
            show: true,
            color: valueColor,
            fontSize: 14,
            fontWeight: 'bold',
            padding: [8, 10, 4, 10],
          },
          label: {
            show: true,
            formatter: '{b}',
          },
        },
      ],
    },
  };

  return (
    <SectionWrapper
      title={<FilterColumnsSelect selectedFilters={selectedFilters} onChangeFilters={setSelectedFilters} />}
      Subtitle={() => <HighPerformingSubtitle selectedMessage={selectedMessage} />}
      Filters={HighPerformingAudiencesFilters({
        isGlobalPercentage: isGlobalPercentage,
        sortBy: sortBy,
        onChangeGlobalPercentage: setIsGlobalPercentage,
        onChangeSortBy: setSortBy,
      })}>
      <GroupedTreemap config={config} data={chartsData} height={selectedFilters.length * 100} />
    </SectionWrapper>
  );
}

export default HighPerformingAudiences;
